import { gql, type TypedDocumentNode } from '@apollo/client';
import { faHourglass2 } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { isBefore } from 'date-fns';
import { useState, type CSSProperties, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons/index.tsx';
import {
  Horizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex/index.tsx';
import {
  LinkBox,
  LinkOverlay,
} from '@sorare/core/src/atoms/navigation/Box/index.tsx';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable/index.tsx';
import { Switcher } from '@sorare/core/src/atoms/navigation/Switcher/index.tsx';
import {
  BodyS,
  BrandM,
  BrandS,
} from '@sorare/core/src/atoms/typography/index.tsx';
import { Chip } from '@sorare/core/src/atoms/ui/Chip/index.tsx';
import { NeonEdge } from '@sorare/core/src/atoms/ui/NeonEdge/index.tsx';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel/index.tsx';
import {
  CornerBadge,
  Position,
} from '@sorare/core/src/components/CornerBadge/index.tsx';
import { ANY_SPORT_MARKET_COMMON_PACKS_GROUP } from '@sorare/core/src/constants/__generated__/routes.ts';
import { useSportContext } from '@sorare/core/src/contexts/sport/index.ts';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft/index.tsx';
import { idFromObject } from '@sorare/core/src/gql/idFromObject.ts';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop.ts';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery.ts';

import { BuyPackButton } from '#src/components/commonCard/BuyPackButton/index.tsx';

import { PackWithBuyButton } from '../PackWithBuyButton/index.tsx';
import type { FeaturedSeriesPackGroup_cardPackGroup } from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ center: true })`
  position: relative;
  margin: 0 calc(-1 * var(--container-padding));
  width: calc(100% + 2 * var(--container-padding));
  background:
    linear-gradient(
      to bottom,
      #0f1c28 0%,
      transparent 20%,
      transparent 80%,
      #0f1c28 100%
    ),
    var(--background-image) bottom center/cover no-repeat;

  padding-bottom: calc(var(--unit) * 10);

  @media ${laptopAndAbove} {
    margin: 0;
    width: 100%;
    height: 100%;
    background: none;
    min-height: 220px;
    padding-bottom: 0;
  }
`;

const StyledBezel = styled(Bezel)`
  width: 100%;
  height: 100%;
  flex-grow: 1;

  background: var(--desktop-background-image) center / cover no-repeat;
`;

const StyledNeonEdge = styled(NeonEdge)`
  width: 100%;
  height: 100%;
  padding: var(--triple-unit);
  @media ${laptopAndAbove} {
    padding: 0;
  }
`;
const Content = styled(Vertical).attrs({ gap: 2, center: true })`
  width: 100%;
  height: 100%;
  padding: 0 var(--quadruple-unit);
  @media ${laptopAndAbove} {
    padding: 0 var(--triple-unit);
  }
`;

const DesktopContent = styled(Vertical).attrs({ gap: 2, center: false })`
  height: 100%;
  flex-grow: 1;
  padding: var(--triple-unit);
`;

const DesktopPack = styled(Vertical).attrs({ gap: 2, center: true })`
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: flex-start;
`;
const Title = styled(BrandM)`
  color: var(--c-white);
`;

const Actions = styled(Horizontal)`
  margin: 0;
`;

const Item = styled(Vertical).attrs({ gap: 2, center: true })`
  width: 100%;
  height: 100%;
  @media ${laptopAndAbove} {
    align-items: flex-start;
    padding: 0 var(--triple-unit);
  }
`;

const PositionedBadge = styled.div`
  display: none;
  @media ${laptopAndAbove} {
    display: block;
    position: absolute;
    top: var(--double-unit);
    right: 0;
    z-index: 1;
    svg {
      margin-right: var(--half-unit);
    }
  }
`;

const DesktopWrapper = ({ children }: { children: ReactNode }) => {
  return (
    <StyledBezel
      style={
        {
          '--background': `linear-gradient(to bottom, transparent 0%, var(--c-black) 100%),
    var(--background-image) bottom -10% center/cover no-repeat`,
          '--box-hover-shadow': `0 0 12px 0 var(--c-wntr-series) inset`,
        } as CSSProperties
      }
    >
      <StyledNeonEdge
        color="var(--c-wntr-series)"
        showHalo="always"
        showNeon="always"
      >
        {children}
      </StyledNeonEdge>
    </StyledBezel>
  );
};

type Props = {
  cardPackGroup: FeaturedSeriesPackGroup_cardPackGroup;
};

const now = new Date();

export const canDisplayFeaturedSeriesPackGroup = (
  cardPackGroup: Nullable<FeaturedSeriesPackGroup_cardPackGroup>
): cardPackGroup is FeaturedSeriesPackGroup_cardPackGroup => {
  if (!cardPackGroup) return false;
  return !!cardPackGroup.cardPacks?.some(
    cardPack => !!cardPack.endDate && isBefore(now, cardPack.endDate)
  );
};

export const FeaturedSeriesPackGroup = ({ cardPackGroup }: Props) => {
  const [index, setIndex] = useState(0);
  const isLaptopAndAbove = useIsLaptop();
  const { generateSportContextPath } = useSportContext();
  const { backgroundUrl, desktopBackgroundUrl, cardPacks } = cardPackGroup;

  const selectedCardPack = cardPacks[index];
  const endDate = selectedCardPack?.endDate;

  const rootStyle = {
    '--background-image': `url(${backgroundUrl})`,
    '--desktop-background-image': `url(${desktopBackgroundUrl})`,
  } as CSSProperties;

  const packUrl = (slug: string) =>
    `${generateSportContextPath(ANY_SPORT_MARKET_COMMON_PACKS_GROUP, {
      params: { group: idFromObject(cardPackGroup.id) },
    })}?pack=${slug}`;

  if (isLaptopAndAbove) {
    return (
      <Root as={LinkBox} style={rootStyle}>
        <DesktopWrapper>
          <DesktopContent>
            <DesktopPack>
              <Title
                className={classNames({ 'text-center': !isLaptopAndAbove })}
              >
                <LinkOverlay as={Link} to={packUrl(selectedCardPack.slug)}>
                  {selectedCardPack.title}
                </LinkOverlay>
              </Title>
              {!!selectedCardPack.description && (
                <BodyS>{selectedCardPack.description}</BodyS>
              )}
              <Actions>
                <BuyPackButton size="medium" cardPack={selectedCardPack}>
                  {(price: ReactNode) => (
                    <>
                      <FormattedMessage
                        id="FeaturedSeriesPackGroup.buyButton"
                        defaultMessage="Get it for {price}"
                        values={{ price }}
                      />
                    </>
                  )}
                </BuyPackButton>
              </Actions>
            </DesktopPack>
            {cardPacks.length > 1 && (
              <Switcher
                numberOfItems={cardPacks.length}
                activeIndex={index}
                onSelect={setIndex}
              />
            )}
          </DesktopContent>
        </DesktopWrapper>
        {!!endDate && (
          <PositionedBadge>
            <CornerBadge color="var(--c-yellow-400)" align={Position.RIGHT}>
              <BrandS>
                <FontAwesomeIcon icon={faHourglass2} />
                <TimeLeft time={endDate} />
              </BrandS>
            </CornerBadge>
          </PositionedBadge>
        )}
      </Root>
    );
  }

  return (
    <Root style={rootStyle}>
      <Content>
        <Scrollable
          itemGap={8}
          overhang={false}
          itemToDisplay={1}
          withScrollButtons
        >
          {cardPacks.map(cardPack => (
            <Item key={cardPack.slug}>
              {!!cardPack.endDate && (
                <Chip color="yellow">
                  <TimeLeft time={cardPack.endDate} />
                </Chip>
              )}
              <Title
                className={classNames({ 'text-center': !isLaptopAndAbove })}
              >
                <LinkOverlay as={Link} to={packUrl(cardPack.slug)}>
                  {cardPack.title}
                </LinkOverlay>
              </Title>
              <BodyS className="text-center">{cardPack.description}</BodyS>
              <PackWithBuyButton
                cardPack={cardPack}
                cardPackGroupId={cardPackGroup.id}
                withEligiblePlayers
                isPremiumPack
                gap={3}
              />
            </Item>
          ))}
        </Scrollable>
      </Content>
    </Root>
  );
};

FeaturedSeriesPackGroup.fragments = {
  cardPackGroup: gql`
    fragment FeaturedSeriesPackGroup_cardPackGroup on CardPackGroup {
      id
      backgroundUrl
      desktopBackgroundUrl
      cardPacks {
        slug
        title
        description
        primaryColor: color(variant: PRIMARY)
        endDate
        ...BuyPackButton_cardPack
        ...PackWithBuyButton_cardPack
      }
    }
    ${BuyPackButton.fragments.cardPack}
    ${PackWithBuyButton.fragments.cardPack}
  ` as TypedDocumentNode<FeaturedSeriesPackGroup_cardPackGroup>,
};
